import { Link } from "react-router-dom";
import { ArrowRight, Quote } from "lucide-react";
import { caseStudies, testimonials } from "@/lib/proof";

/**
 * Renders real proof (case studies + testimonials) when present. Returns null
 * while the proof data is empty, so nothing fabricated ever ships. Populate
 * src/lib/proof.ts to light this up.
 */
const ProofSection = ({
  heading = "Proof from real engagements",
  intro = "Outcomes from work SVM has delivered.",
}: {
  heading?: string;
  intro?: string;
}) => {
  if (caseStudies.length === 0 && testimonials.length === 0) {
    return null;
  }

  return (
    <section className="bg-white py-16">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="mb-10 max-w-3xl">
          <h2 className="svm-display-tight mb-4 text-4xl md:text-5xl">{heading}</h2>
          <p className="font-medium text-slate-600">{intro}</p>
        </div>

        {caseStudies.length > 0 && (
          <div className="grid gap-4 lg:grid-cols-2">
            {caseStudies.map((study) => (
              <article
                key={study.slug}
                className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm"
              >
                <p className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-rose-700">
                  {study.service}
                </p>
                {study.client && <h3 className="mb-3 text-2xl font-black">{study.client}</h3>}
                <p className="mb-4 font-medium text-slate-700">{study.summary}</p>
                <ul className="mb-4 grid gap-2">
                  {study.outcomes.map((outcome) => (
                    <li key={outcome} className="text-sm font-semibold text-slate-700">
                      • {outcome}
                    </li>
                  ))}
                </ul>
                {study.entryPath && (
                  <Link
                    to={study.entryPath}
                    className="inline-flex items-center gap-2 text-sm font-bold text-slate-950 hover:text-rose-700"
                  >
                    Explore this path
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                )}
              </article>
            ))}
          </div>
        )}

        {testimonials.length > 0 && (
          <div className="mt-6 grid gap-4 lg:grid-cols-3">
            {testimonials.map((testimonial) => (
              <figure
                key={testimonial.quote}
                className="rounded-3xl border border-slate-200 bg-slate-50 p-6"
              >
                <Quote className="mb-4 h-6 w-6 text-rose-700" />
                <blockquote className="font-medium text-slate-800">"{testimonial.quote}"</blockquote>
                <figcaption className="mt-4 text-sm font-bold text-slate-950">
                  {testimonial.author}
                  {testimonial.role && (
                    <span className="font-medium text-slate-600">
                      {" "}
                      — {testimonial.role}
                      {testimonial.org ? `, ${testimonial.org}` : ""}
                    </span>
                  )}
                </figcaption>
              </figure>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ProofSection;
